import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Lang } from '../models';
import { InternalLocationItemModel } from '../models/Ndtrc';
import {
  describeLocation,
  fetchLocationById,
  fetchLocationsByQuery,
  getLocationTitle,
} from '../helpers/fetchLocations';
import classes from './LocationSelector.module.scss';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

interface LocationSelectorProps {
  /** Id of the currently selected location, or null. */
  value: string | null;
  lang: Lang;
  onSelect: (location: InternalLocationItemModel | null) => void;
  filterOnUserOrganisations?: string[];
  myUserOrganisations?: string[];
  minimalCardDisplay?: boolean;
}

const LocationSelector: React.FC<LocationSelectorProps> = ({
  value,
  lang,
  onSelect,
  filterOnUserOrganisations,
  myUserOrganisations,
}) => {
  const [selected, setSelected] = useState<InternalLocationItemModel | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<InternalLocationItemModel[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Guards against an earlier, slower search overwriting a later one.
  const searchId = useRef(0);

  const organisations = filterOnUserOrganisations?.length
    ? filterOnUserOrganisations
    : myUserOrganisations;

  // The form only hands back the id, so a location chosen earlier (or restored
  // into the form) has to be looked up again to be shown by name.
  useEffect(() => {
    let cancelled = false;

    if (!value) {
      setSelected(null);
      return;
    }
    if (selected?.id === value) {
      return;
    }

    fetchLocationById(value)
      .then((location) => {
        if (!cancelled && location) {
          setSelected(location);
        }
      })
      .catch(() => {
        // Falling back to the search box is better than blocking the form.
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const runSearch = useCallback(
    async (query: string) => {
      const id = ++searchId.current;
      setIsSearching(true);
      setError(null);
      try {
        const locations = await fetchLocationsByQuery(query, organisations);
        if (id === searchId.current) {
          setResults(locations);
        }
      } catch {
        if (id === searchId.current) {
          setError('Searching for locations failed. Please try again.');
          setResults(null);
        }
      } finally {
        if (id === searchId.current) {
          setIsSearching(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organisations?.join(';')]
  );

  useEffect(() => {
    const query = searchTerm.trim();
    if (selected || query.length < MIN_QUERY_LENGTH) {
      setResults(null);
      setIsSearching(false);
      return;
    }

    const timer = setTimeout(() => runSearch(query), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm, selected, runSearch]);

  const handleSelect = (location: InternalLocationItemModel) => {
    setSelected(location);
    setSearchTerm('');
    setResults(null);
    onSelect(location);
  };

  const handleRemove = () => {
    setSelected(null);
    setSearchTerm('');
    setResults(null);
    onSelect(null);
  };

  if (selected) {
    const address = describeLocation(selected);
    return (
      <div className={classes.locationSelector}>
        <div className={classes.selected}>
          <div className={classes.selectedInfo}>
            <p>
              <strong>{getLocationTitle(selected, lang)}</strong>
            </p>
            {address && <p className={classes.resultAddress}>{address}</p>}
          </div>
          <button type="button" className={classes.button} onClick={handleRemove}>
            Choose another location
          </button>
        </div>
      </div>
    );
  }

  const query = searchTerm.trim();

  return (
    <div className={classes.locationSelector}>
      <div className={classes.searchRow}>
        <input
          type="text"
          className={classes.searchInput}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search for a venue by name"
          aria-label="Search for a venue by name"
        />
      </div>

      {error && <div className={classes.error}>{error}</div>}

      {!error && isSearching && <p className={classes.status}>Searching...</p>}

      {!error && !isSearching && query.length > 0 && query.length < MIN_QUERY_LENGTH && (
        <p className={classes.status}>Type at least {MIN_QUERY_LENGTH} characters.</p>
      )}

      {!error && !isSearching && results?.length === 0 && (
        <p className={classes.status}>
          No venues found for "{query}". You can add the address by hand instead.
        </p>
      )}

      {!error && !!results?.length && (
        <ul className={classes.results}>
          {results.map((location) => {
            const address = describeLocation(location);
            return (
              <li key={location.id || location.trcid} className={classes.result}>
                <button
                  type="button"
                  className={classes.resultButton}
                  onClick={() => handleSelect(location)}
                >
                  <span className={classes.resultTitle}>
                    {getLocationTitle(location, lang)}
                  </span>
                  {address && <span className={classes.resultAddress}> — {address}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LocationSelector;
